import { withProtectedResource } from '@delmonicos/shared-design-system-react/src/components/protected-resources/with-protected-resource.helper';
import { safeHasAccessTo } from '@delmonicos/shared-sdk/libs/cpms/src/clients/utils/helper/has-access-to';
import { ActionsByResource } from '@delmonicos/shared-sdk/libs/cpms/src/libs/identity-service-lib/src/wrapper/enums/action.enum';
import { Resource } from '@delmonicos/shared-sdk/libs/cpms/src/libs/identity-service-lib/src/wrapper/enums/resource.enum';
import { LocationModel } from '@delmonicos/shared-sdk/libs/cpms/src/libs/ocpi/src/ocpi-cpo-api/models/locations/location.model';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';

import { permissions } from '@app/core/authentication/authentication.slice';
import { useAppSelector } from '@app/core/hooks';
import { useEmspBoGetCposQuery } from '@app/core/service/emsp-bo-cpo.service';
import { useEmspBoGetLocationQuery } from '@app/core/service/emsp-bo-location.service';
import { EmspBoLocationDetailsView } from '@app/feature/emsp-bo-location/emsp-bo-location-details/emsp-bo-location-details.view';
import { EmspBoLocationContextProvider } from '@app/feature/emsp-bo-location/emsp-bo-location.context';
import { useSetPageTitleContext } from '@shared/layout/page-layout/page-layout-title.context-provider';

interface EmspBoLocationDetailsControllerProps {
  resource: LocationModel;
}

const EmspBoLocationDetailsController = ({
  resource,
}: EmspBoLocationDetailsControllerProps): React.JSX.Element => {
  // CONTEXT ///////////////////////////////////////////////////////////////////

  const { t } = useTranslation();

  // USER PERMISSIONS //////////////////////////////////////////////////////////

  // Nothing to do

  // ACTIONS ///////////////////////////////////////////////////////////////////

  // Nothing to do

  // PAGE TITLE ////////////////////////////////////////////////////////////////

  const setPageTitle = useSetPageTitleContext();

  useEffect(() => {
    setPageTitle(resource?.name ?? t('$.emsp-bo-location-details.title'));
  }, [setPageTitle, resource, t]);

  // PAGE ACTIONS //////////////////////////////////////////////////////////////

  // Nothing to do

  // PAGE BREADCRUMB ///////////////////////////////////////////////////////////

  // Nothing to do

  // HANDLERS //////////////////////////////////////////////////////////////////

  // Nothing to do

  // RENDER ////////////////////////////////////////////////////////////////////

  return (
    <EmspBoLocationContextProvider data={resource}>
      <EmspBoLocationDetailsView />
    </EmspBoLocationContextProvider>
  );
};

export const EmspBoLocationDetailsPage = (): React.JSX.Element => {
  const userPermissions = useAppSelector(permissions);
  const params = useParams();

  // The API requires a valid UUID for cpoId. We fetch the CPO list and
  // auto-select the first one, then skip the location query until it is resolved.
  const { data: cposData } = useEmspBoGetCposQuery({ limit: 100, offset: 0 });
  const firstCpoId = cposData?.docs.at(0)?.id ?? '';
  const hasCpoId = firstCpoId !== '';

  const queryState = useEmspBoGetLocationQuery(
    { locationId: params.id ?? '', cpoId: firstCpoId },
    { skip: !hasCpoId },
  );

  // Treat uninitialized (waiting for first CPO) as loading so the UI shows a skeleton.
  // Cast required: RTK Query uses discriminated union types for isLoading/isUninitialized.
  const effectiveQueryState = {
    ...queryState,
    isLoading: queryState.isLoading || queryState.isUninitialized,
  } as unknown as typeof queryState;

  const { Component } = withProtectedResource<LocationModel, {}, LocationModel>(
    EmspBoLocationDetailsController,
    effectiveQueryState,
    {
      isAuthorized: () =>
        safeHasAccessTo(userPermissions, {
          resourceName: Resource.EMSP_BO,
          action: ActionsByResource.EMSP_BO.READ_LOCATION,
        }),
    },
  )({});

  return Component;
};
